// src/pages/demandes/MesDemandes.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Package, Calendar, User, Clock, CheckCircle, 
  XCircle, AlertCircle, Eye, Plus, Filter
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function MesDemandes() {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchDemandes = async () => {
    setLoading(true);
    setError('');
    try {
      const url = statusFilter
        ? `${API_URL}/profile/my-demandes?status=${statusFilter}`
        : `${API_URL}/profile/my-demandes`;

      const res = await axios.get(url, { headers });
      const data = res.data?.data?.data || res.data?.data || [];
      setDemandes(data);
    } catch (err) {
      console.error('Erreur chargement demandes:', err);
      setError('Impossible de charger vos demandes. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemandes();
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    const config = {
      en_attente: { 
        label: 'En attente', 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200', 
        icon: Clock 
      },
      validee: { 
        label: 'Validée', 
        color: 'bg-green-50 text-green-700 border-green-200', 
        icon: CheckCircle 
      },
      refusee: { 
        label: 'Refusée', 
        color: 'bg-red-50 text-red-700 border-red-200', 
        icon: XCircle 
      },
      annulee: { 
        label: 'Annulée', 
        color: ' text-gray-700 border-gray-200', 
        icon: AlertCircle 
      },
      pret_recuperation: { 
        label: 'Prête à récupérer', 
        color: 'bg-blue-50 text-blue-700 border-blue-200', 
        icon: Package 
      },
      en_preparation: { 
        label: 'En préparation', 
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200', 
        icon: Package 
      },
    };

    const item = config[status] || { 
      label: status || 'Inconnu', 
      color: ' text-gray-700 border-gray-200', 
      icon: AlertCircle 
    };
    const Icon = item.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${item.color}`}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{item.label}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Chargement de vos demandes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen  flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 mb-6">{error}</p>
          <button 
            onClick={fetchDemandes}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mes Demandes</h1>
              <p className="text-gray-600 mt-1">
                {demandes.length} demande{demandes.length > 1 ? 's' : ''} trouvée{demandes.length > 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none"
                >
                  <option value="">Tous les statuts</option>
                  <option value="en_attente">En attente</option>
                  <option value="validee">Validées</option>
                  <option value="pret_recuperation">Prêtes à récupérer</option>
                  <option value="refusee">Refusées</option>
                  <option value="annulee">Annulées</option>
                </select>
              </div>

              <Link
                to="/demandes/nouvelle"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nouvelle demande
              </Link>
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        {demandes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium mb-4">Aucune demande trouvée</p>
            <Link
              to="/demandes/nouvelle"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Créer ma première demande
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {demandes.map((demande) => (
              <div
                key={demande.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                    {/* N° + Date */}
                    <div className="md:col-span-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-50 text-blue-700 font-semibold px-3 py-1 rounded-lg">
                          #{demande.numero || demande.id}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(demande.created_at), 'dd MMM yyyy', { locale: fr })}
                        </div>
                      </div>
                    </div>

                    {/* Validateur */}
                    <div className="md:col-span-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {demande.validateur?.nom || 'Non assigné'}
                        </span>
                      </div>
                    </div>

                    {/* Statut */}
                    <div className="md:col-span-3">
                      {getStatusBadge(demande.status || demande.statut)}
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-2">
                      <Link
                        to={`/demande-timeline/${demande.id}`}
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5  hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Suivre
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}